/**
 * PATH: packages/auth/src/rbac-state.ts
 * FILE_ID: AUTH-RBAC-STATE-001
 * PURPOSE: useRBAC hook — fetches, caches, and exposes RBAC permission state
 * WHY: Extracted from usePermission.ts (was ~190 lines). This is the heaviest
 *      hook in the permission system: it manages backend fetches, localStorage
 *      fallbacks, token resolution, and storage-event listeners.
 * FLOW:
 *   ┌────────────────┐    ┌──────────────────┐    ┌───────────┐
 *   │ authStore/      │ →  │ fetchPermissions  │ →  │ RBACState │
 *   │ localStorage    │    │ (API + fallback)  │    │           │
 *   └────────────────┘    └──────────────────┘    └───────────┘
 * DEPENDENCIES:
 *   - react: useState, useEffect, useCallback, useMemo
 *   - ./authStore: useAuthStore (user + org context)
 *   - ./permission-types: UserPermissions, RBACState
 * RELATED:
 *   - AUTH-RBAC-TYPES-001 (permission-types.ts): type definitions
 *   - AUTH-RBAC-COALESCE-001 (rbac-request-coalesce.ts): collapses the N
 *     concurrent permission fetches (one per mounted consumer) into one request
 *   - packages/auth/src/usePermission.ts: consumer hooks
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useAuthStore } from './authStore'
import { getAuthCookie } from './cookieAuth'
import { cacheJobScopeGroupingEnabled } from './scopeGroupingFeature'
import { coalesceRequest } from './rbac-request-coalesce'
import type { UserPermissions, RBACState } from './permission-types'

/**
 * Fetch + validate the raw RBAC permissions payload. Kept module-scope (not a
 * closure) so concurrent callers route through one shared, coalesced request.
 * Throws on a non-OK response so each caller still runs its own error fallback.
 */
async function fetchRbacPermissions(url: string, token: string | null) {
  const response = await fetch(url, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  })
  if (!response.ok) {
    const errorText = await response.text()
    throw new Error(`Failed to fetch permissions: ${response.status} - ${errorText}`)
  }
  return response.json()
}

/**
 * Main hook for accessing RBAC state.
 * Fetches and caches user permissions.
 *
 * @returns RBACState - Full RBAC state including permissions, role, etc.
 *
 * @example
 * const { permissions, role, isOrgAdmin, accessibleModules } = useRBAC()
 */
export function useRBAC(): RBACState {
  const { user: authStoreUser, organisationId: authStoreOrgId } = useAuthStore()

  // Memoize legacy localStorage reads to prevent infinite re-renders
  // CRITICAL: Reading localStorage on every render creates new object references
  // which breaks dependency arrays and causes infinite loops
  const [legacyUser, setLegacyUser] = useState<Record<string, unknown> | null>(() => {
    if (typeof localStorage === 'undefined') return null
    try {
      return JSON.parse(localStorage.getItem('ProfileData') || 'null')
    } catch {
      return null
    }
  })

  const [legacyOrgId, setLegacyOrgId] = useState<string | null>(() => {
    if (typeof localStorage === 'undefined') return null
    return localStorage.getItem('CurrentTenantId') || localStorage.getItem('org_id')
  })

  // Listen for storage changes (e.g., from other tabs or login/logout)
  useEffect(() => {
    const handleStorageChange = () => {
      try {
        const newUser = JSON.parse(localStorage.getItem('ProfileData') || 'null')
        const newOrgId = localStorage.getItem('CurrentTenantId') || localStorage.getItem('org_id')
        setLegacyUser(newUser)
        setLegacyOrgId(newOrgId)
      } catch {
        // Ignore parse errors
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [])

  // Use authStore values, fallback to legacy
  const user = authStoreUser || legacyUser
  const organisationId = authStoreOrgId || legacyOrgId
  const latestRequestKey = useRef('')

  // Stable user identifier for dependency array (avoids object reference issues)
  const userEmail = user?.email || null

  const [state, setState] = useState<{
    authorisedOrganisationId: string | null
    permissions: UserPermissions
    isGlobalAdmin: boolean
    isOrgAdmin: boolean
    role: string | null
    hierarchyLevel: number
    accessibleModules: string[]
    hasFinanceOrgWide: boolean
    isOrgAdminUser: boolean
    isBillingAdmin: boolean
    enableJobScopeGrouping: boolean
    accessPreset: string
    navAllowlist: string[] | null
    loading: boolean
    error: string | null
  }>({
    authorisedOrganisationId: null,
    permissions: {},
    isGlobalAdmin: false,
    isOrgAdmin: false,
    role: null,
    hierarchyLevel: 999,
    accessibleModules: [],
    hasFinanceOrgWide: false,
    isOrgAdminUser: false,
    isBillingAdmin: false,
    enableJobScopeGrouping: false,
    accessPreset: 'full',
    navAllowlist: null,
    loading: true,
    error: null,
  })

  const fetchPermissions = useCallback(async () => {
    const requestKey = `${userEmail || ''}::${organisationId || ''}`
    latestRequestKey.current = requestKey
    console.debug('[RBAC] fetchPermissions called:', {
      hasUser: !!user,
      userEmail: user?.email,
      authStoreUser: !!authStoreUser,
      legacyUser: !!legacyUser,
      organisationId
    })

    if (!user) {
      console.debug('[RBAC] No user found, skipping permission fetch')
      setState(prev => ({
        ...prev,
        authorisedOrganisationId: null,
        permissions: {},
        isGlobalAdmin: false,
        isOrgAdmin: false,
        role: null,
        hierarchyLevel: 999,
        accessibleModules: [],
        hasFinanceOrgWide: false,
        isOrgAdminUser: false,
        isBillingAdmin: false,
        enableJobScopeGrouping: false,
        accessPreset: 'full',
        navAllowlist: null,
        loading: false,
        error: null,
      }))
      return
    }

    try {
      setState(prev => ({
        ...prev,
        authorisedOrganisationId: null,
        loading: true,
        error: null,
      }))

      const backendUrl = import.meta.env?.VITE_API_URL || 'https://quotech-backend.quotech.ai'
      let url = `${backendUrl}/api/rbac/permissions/`
      if (organisationId) {
        url += `?org_id=${organisationId}`
      }

      // Token resolution — matches setupHubApi.ts: cookie first, then store, then legacy localStorage
      const cookieToken = getAuthCookie()
      const zustandToken = useAuthStore.getState().accessToken

      const token = cookieToken ||
        zustandToken ||
        localStorage.getItem('IdToken') ||
        localStorage.getItem('accessTokennew') ||
        localStorage.getItem('AccessToken') ||
        localStorage.getItem('accessToken') ||
        localStorage.getItem('token')

      const tokenSource = cookieToken ? 'cookie' : zustandToken ? 'zustand' : 'localStorage'
      console.debug('[RBAC] Fetching permissions:', {
        url,
        hasToken: !!token,
        tokenSource,
        organisationId
      })

      console.debug('[RBAC] Making fetch request...')

      // Coalesce concurrent identical fetches: when several permission-gated
      // components mount together they share ONE in-flight request instead of
      // each firing its own. Keyed by user + org so a different user/org never
      // reuses another's permissions. No result caching — once settled the key
      // is released, so refresh / refetch-on-change behave exactly as before.
      const data = await coalesceRequest(requestKey, () => fetchRbacPermissions(url, token))
      if (latestRequestKey.current !== requestKey) return

      console.debug('[RBAC] ✅ Received permissions:', {
        isGlobalAdmin: data.is_global_admin,
        isOrgAdmin: data.is_org_admin,
        role: data.role,
        userId: data.user_id,
        email: data.email,
      })

      let accessibleModules: string[] = data.accessible_modules || []
      const isGlobalAdmin = data.is_global_admin || false
      const isOrgAdmin = data.is_org_admin || false
      if ((isGlobalAdmin || isOrgAdmin) && !accessibleModules.includes('developer')) {
        accessibleModules = [...accessibleModules, 'developer']
      }

      // ── RBAC race hardening (BUG-2026-06-26-RBAC-ORG-RACE) ──
      // The permissions endpoint answers org-scoped questions ONLY when an
      // org_id is supplied. On the first paint after login the active org is
      // often not resolved yet (authStoreOrgId AND legacyOrgId both null), so
      // THIS call goes out WITHOUT org_id and the backend can only return
      // global-scope facts — every org-scoped flag comes back false
      // (is_org_admin=false, empty accessible_modules). Settling that as the
      // user's REAL permissions transiently flips admin-gated surfaces
      // (Settings → Organisation/Integrations, hub guards) to "denied/locked"
      // for an org owner until the org resolves and this effect refetches WITH
      // org_id. Unless the user is a GLOBAL admin (status is org-independent),
      // treat the org-less answer as "still loading" so consumers stay
      // optimistic — consistent with how the layout already treats `loading`
      // (no hub filtering while loading). deps include organisationId, so the
      // effect re-runs and settles real permissions the moment the org pins.
      if (!organisationId && !isGlobalAdmin) {
        console.debug('[RBAC] org_id unresolved — deferring org-scoped permissions (keeping loading)')
        setState(prev => ({ ...prev, loading: true, error: null }))
        return
      }

      const enableJobScopeGrouping = Boolean(data.enable_job_scope_grouping)
      if (organisationId) {
        cacheJobScopeGroupingEnabled(organisationId, enableJobScopeGrouping)
      }

      // Capability toggles (RBAC V2). Owner/global-admin already arrive True
      // from the backend; default to false when absent so an older backend
      // (pre-MR20) safely degrades to "no extra capability".
      const hasFinanceOrgWide = Boolean(data.has_finance_org_wide) || isGlobalAdmin
      const isOrgAdminUser = Boolean(data.is_org_admin_user) || isGlobalAdmin
      const isBillingAdmin = Boolean(data.is_billing_admin) || isGlobalAdmin

      // Coarse onboarding access level + the sidebar nav allow-list it implies.
      // Owners/global admins arrive 'full' from the backend (no clamp). A
      // pre-preset backend omits both fields → degrade to 'full' / no limit so
      // existing users are never accidentally restricted.
      const accessPreset = (isGlobalAdmin ? 'full' : (data.access_preset || 'full')) as string
      const diaryOnlyNav = ['diary', 'emails', 'calendar']
      const navAllowlist = (isGlobalAdmin || accessPreset === 'full')
        ? null
        : accessPreset === 'diary_only'
          ? (Array.isArray(data.nav_allowlist) && data.nav_allowlist.length > 0
            ? data.nav_allowlist as string[]
            : diaryOnlyNav)
          : (Array.isArray(data.nav_allowlist) ? data.nav_allowlist as string[] : null)

      setState({
        authorisedOrganisationId: organisationId,
        permissions: data.permissions || {},
        isGlobalAdmin,
        isOrgAdmin,
        role: data.role || null,
        hierarchyLevel: data.hierarchy_level ?? 999,
        accessibleModules,
        hasFinanceOrgWide,
        isOrgAdminUser,
        isBillingAdmin,
        enableJobScopeGrouping,
        accessPreset,
        navAllowlist,
        loading: false,
        error: null,
      })
    } catch (err) {
      if (latestRequestKey.current !== requestKey) return
      console.error('[RBAC] Failed to fetch permissions:', err)
      // Don't block UI on RBAC failure - fall back to checking user object for admin status
      const profileData: Record<string, unknown> = typeof localStorage !== 'undefined'
        ? JSON.parse(localStorage.getItem('ProfileData') || '{}')
        : {}
      const userRole = String(user?.role || profileData?.role || '')
      const adminRoles = ['owner', 'admin', 'superadmin', 'org_owner', 'org_admin', 'organisation admin', 'global_admin']
      const fallbackIsAdmin = adminRoles.includes(userRole.toLowerCase())

      setState(prev => ({
        ...prev,
        authorisedOrganisationId: null,
        loading: false,
        // Use fallback admin check if RBAC API fails
        isOrgAdmin: fallbackIsAdmin,
        isGlobalAdmin: userRole.toLowerCase() === 'global_admin',
        error: err instanceof Error ? err.message : 'Unknown error',
      }))
    }
  // CRITICAL: Use stable primitive values (userEmail, organisationId) instead of object references
  // to prevent infinite re-render loops
  }, [userEmail, organisationId])

  // Fetch permissions on mount and when user/org changes
  useEffect(() => {
    fetchPermissions()
  }, [fetchPermissions])

  return useMemo(() => ({
    ...state,
    refresh: fetchPermissions,
  }), [state, fetchPermissions])
}
